import {Component, Inject, ElementRef, ViewChild} from '@angular/core';
import {DialogResult, DialogResultType} from '../DialogResult';
import {MAT_DIALOG_DATA, MatDialog, MatDialogRef} from '@angular/material/dialog';
import {Observable} from 'rxjs/internal/Observable';
import {ShareState} from '../../elements/share-editor/share-editor.component';
import {US_PreFilter} from '../../api/models/us_pre-filter';
import {US_SubHeader} from '../../api/models/us_sub-header';
import {FilterKeys} from '../../api/models/filter-keys';
import {FilterEditorDialogComponent, FilterEditorDialogResult} from '../filter-editor-dialog/filter-editor-dialog.component';
import {PreFilterService} from '../../api/services/pre-filter.service';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {DodEndPointEx} from "../../api/models/dod-end-point-ex";
import {DataOnDemandService} from "../../api/services/data-on-demand.service";

export class PreFilterDialogData {
  preFilter: US_PreFilter;
  subscriptions: US_SubHeader[];
  shareState: ShareState;
  allEndPoints?: DodEndPointEx[];
}

export class PreFilterDialogResult {
  preFilter: US_PreFilter;
  shareState: ShareState;
}

@Component({
  selector: 'pre-filter-editor-dialog',
  templateUrl: './pre-filter-editor-dialog.component.html',
  styleUrls: ['./pre-filter-editor-dialog.component.css'],
})
export class PreFilterEditorDialogComponent {

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  public result: PreFilterDialogResult;
  public selectedSubscriptionId: number = -1;
  public shareState: ShareState;

  public errorMessages: string[] = [];
  public statusMessage: string | undefined = undefined;
  public allEndPoints: DodEndPointEx[] = [];

  constructor(
    public preFilterService: PreFilterService,
    public dodService: DataOnDemandService,
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<PreFilterEditorDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PreFilterDialogData
  ) {

    this.allEndPoints = data.allEndPoints || [];

    this.result = {
      preFilter: JSON.parse(JSON.stringify(data.preFilter)),
      shareState: data.shareState
    }

    const subId = this.result.preFilter.subscriptionId;
    if (subId !== undefined && subId !== null && subId > 0) {
      this.selectedSubscriptionId = subId;
    } else {
      this.selectedSubscriptionId = this.data.subscriptions[0]?.subscriptionId;
    }
    this.shareState = data.shareState;

    this.Result = new DialogResult();
    this.Result.ResultType = DialogResultType.Cancel;
    this.Result.ResultData = null;
  }

  handleFileInput(files: FileList) {
    if (this.isFilterEmpty(this.result.preFilter.filterData)) {
      this.processTemplate(files);
    } else {
      ConfirmDialogComponent.showDialog(this.dialog, 'Overwrite Filter?', 'Are you sure you want to overwrite the current filter?').subscribe(result => {
        if (result.ResultType === DialogResultType.Ok) {
          this.processTemplate(files);
        } else {
          if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        }
      });
    }
  }

  private isFilterEmpty(filterData: string | undefined): boolean {
    if (!filterData || filterData === '{}') {
      return true;
    }

    try {
      const keys = JSON.parse(filterData);
      // If it's the default "Last Month" filter with no other selections, we consider it empty
      return keys.dateRangeType === 1 &&
        keys.dateRange === 2 &&
        (!keys.titles || keys.titles.length === 0) &&
        (!keys.occupations || keys.occupations.length === 0) &&
        (!keys.companies || keys.companies.length === 0) &&
        (!keys.industries || keys.industries.length === 0) &&
        (!keys.locations || keys.locations.length === 0) &&
        (!keys.metroGroups || keys.metroGroups.length === 0) &&
        (!keys.attributes || keys.attributes.length === 0) &&
        (!keys.educationInstitutions || keys.educationInstitutions.length === 0);
    } catch (e) {
      return false;
    }
  }

  private processTemplate(files: FileList) {
    this.errorMessages = [];
    this.statusMessage = undefined;
    const uploadFile = files.item(0);
    if (uploadFile) {
      if (uploadFile.size > 10000000) {
        this.errorMessages.push('ERROR: File size is too large. Max file size is 10MB.');
        if (this.fileInput?.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        return;
      }
      this.statusMessage = 'Processing ' + uploadFile.name + ', Please Wait...';
      this.preFilterService.preFilterProcessPreFilterTemplate(uploadFile).subscribe({
        next: result => {
          if (result.hasError) {
            this.errorMessages.push('ERROR: ' + result.errorMessage);
            this.statusMessage = undefined;
            if (this.fileInput?.nativeElement) {
              this.fileInput.nativeElement.value = '';
            }
          } else {
            this.result.preFilter.filterData = result.filter;
            this.statusMessage = 'SUCCESS: Template Applied';
            if (this.fileInput?.nativeElement) {
              this.fileInput.nativeElement.value = '';
            }
          }
        },
        error: err => {
          this.errorMessages.push('ERROR: Upload failed ' + err);
          this.statusMessage = undefined;
          if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        },
        complete: () => {
          // Clear file input so the same file can be re-selected if needed
          if (this.fileInput?.nativeElement) {
            this.fileInput.nativeElement.value = '';
          }
        }
      });
    }
  }

  public Result: DialogResult;

  static showDialog(
    dialog: MatDialog,
    preFilter: US_PreFilter,
    shareState: ShareState,
    subscriptions: US_SubHeader[],
    allEndPoints?: DodEndPointEx[]
  ): Observable<DialogResult> {
    const params: PreFilterDialogData = {
      preFilter,
      shareState,
      subscriptions,
      allEndPoints
    };

    const dialogRef = dialog.open(PreFilterEditorDialogComponent, {
      disableClose: true,
      width: '900px',
      data: params,

    });

    return dialogRef.afterClosed();
  }

  onCancelClick(): void {
    this.dialogRef.close(this.Result);
  }

  onSavePreFilter(): void {
    this.closeDialog();
  }

  closeDialog(): void {
    if (!this.result.preFilter.filterData || this.result.preFilter.filterData === '' || this.result.preFilter.filterData === '{}') {
      const defaultFilter: FilterKeys = {
        titles: [],
        occupations: [],
        companies: [],
        industries: [],
        locations: [],
        metroGroups: [],
        attributes: [],
        educationInstitutions: [],
        jobPosting: {
          postingsWithCompensation: null,
          compensationRateMinimum: null,
          compensationRateMaximum: null
        },
        sourceJobPosting: {
          primaryJobType: null,
          jobStatus: null,
          minMidSalary: null,
          maxMidSalary: null,
          urgent: null,
          sponsored: null,
          applyOnIndeed: null,
          remoteStatus: null
        },
        dateRangeType: 1,
        dateRange: 2,
        flags: {
          useCleanLocations: 'true',
          useMetroGroups: 'false'
        }
      };
      this.result.preFilter.filterData = JSON.stringify(defaultFilter);
    }

    this.result.preFilter.subscriptionId = this.selectedSubscriptionId;
    this.result.shareState = this.shareState;

    this.Result.ResultType = DialogResultType.Ok;
    this.Result.ResultData = this.result;
    this.dialogRef.close(this.Result);
  }

  editFilter(): boolean {
    FilterEditorDialogComponent.showDialog(
      this.dialog,
      this.result.preFilter.filterData,
      null,
      false,
      this.allEndPoints
    ).subscribe(result => {
      if (result.ResultType === DialogResultType.Ok) {
        const results: FilterEditorDialogResult = result.ResultData;
        this.result.preFilter.filterData = results.filterData;
      }
    });

    return false;
  }
}
